// Class Schedule Block View Component
// Read-only view of class schedule for public portal pages
import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';

const ClassScheduleBlockView = ({ content, userId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper to determine if a color is light or dark
  const isLightColor = (color) => {
    if (!color || color === 'transparent') return true;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 186; // Higher threshold for better contrast
  };

  const getTextColorClass = (bgColor) => {
    return isLightColor(bgColor) ? 'text-gray-900' : 'text-white';
  };

  const getSubTextColorClass = (bgColor) => {
    return isLightColor(bgColor) ? 'text-gray-700' : 'text-gray-100';
  };

  const getFontFamily = (fontName) => {
    if (!fontName || fontName === 'system') {
      return 'ui-sans-serif, system-ui, sans-serif';
    }
    return `"${fontName}", sans-serif`;
  };

  useEffect(() => {
    fetchSessions();
  }, [userId, content?.months]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      if (!userId) {
        console.error('No userId provided to ClassScheduleBlockView');
        setLoading(false);
        return;
      }
      const response = await fetch(`/api/class-sessions/public?months=${content?.months || 3}&userId=${userId}`);
      const data = await response.json();
      setSessions(data.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get upcoming sessions with filters applied
  const getUpcomingSessions = () => {
    return sessions.filter(session => {
      const cls = session.class;
      if (!cls) return false;

      // Category filter
      if (content?.categoryFilter && cls.category !== content.categoryFilter) return false;

      // Skill level filter
      if (content?.skillLevelFilter && cls.skill_level !== content.skillLevelFilter) return false;

      // Instructor filter
      if (content?.instructorFilter && cls.instructor !== content.instructorFilter) return false;

      // Tags filter
      if (content?.tagsFilter && content.tagsFilter.length > 0) {
        const classTags = cls.tags || [];
        const hasMatchingTag = content.tagsFilter.some(tag =>
          classTags.some(classTag =>
            classTag.toLowerCase().includes(tag.toLowerCase())
          )
        );
        if (!hasMatchingTag) return false;
      }

      return true;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7A99D8]"></div>
      </div>
    );
  }

  const upcomingSessions = getUpcomingSessions();

  // Group sessions by class and get date ranges
  const getClassesWithDateRanges = () => {
    const classMap = {};

    upcomingSessions.forEach(session => {
      const classId = session.class?.id;
      if (!classId) return;

      if (!classMap[classId]) {
        classMap[classId] = {
          class: session.class,
          sessions: [],
          // Use actual first/last session dates for the full class range
          startDate: session.class?.first_session_date || session.session_date,
          endDate: session.class?.last_session_date || session.session_date,
          startTime: session.start_time,
          currentEnrollment: session.class?.current_enrollment || 0,
          maxCapacity: session.class?.max_capacity || 0
        };
      }

      classMap[classId].sessions.push(session);
    });

    // Sort by start date
    return Object.values(classMap).sort((a, b) =>
      new Date(a.startDate) - new Date(b.startDate)
    );
  };

  const classesWithRanges = getClassesWithDateRanges();

  if (classesWithRanges.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <Calendar className="w-12 h-12 text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">No upcoming classes</p>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    // Parse date string as local time to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    return minutes === 0 ? `${hour12}${period}` : `${hour12}:${minutes.toString().padStart(2, '0')}${period}`;
  };

  // Helper to parse date string as local time
  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Group classes by month
  const getClassesByMonth = () => {
    const monthMap = {};

    classesWithRanges.forEach(item => {
      const date = parseLocalDate(item.startDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

      if (!monthMap[monthKey]) {
        monthMap[monthKey] = {
          label: monthLabel,
          classes: []
        };
      }
      monthMap[monthKey].classes.push(item);
    });

    // Sort by month key and return as array
    return Object.entries(monthMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, value]) => value);
  };

  const classesByMonth = getClassesByMonth();

  const cardBg = content?.cardBackgroundColor || '#ffffff';
  const containerBg = content?.backgroundColor || '#ffffff';

  return (
    <>
      <h3
        className={`font-semibold text-xl mb-4 ${getTextColorClass(containerBg)}`}
        style={{ fontFamily: getFontFamily(content?.titleFontFamily || content?.fontFamily) }}
      >
        {content?.title || 'Upcoming Classes'}
      </h3>
      <div className="space-y-8" style={{ fontFamily: getFontFamily(content?.contentFontFamily) }}>
        {classesByMonth.map((monthGroup) => (
          <div key={monthGroup.label} className="mb-6">
            <h4 className={`font-medium text-sm mb-2 uppercase tracking-wide flex items-center gap-2 ${getSubTextColorClass(containerBg)}`}>
              <Calendar className="w-4 h-4" />
              {monthGroup.label}
            </h4>
            <div className="space-y-3">
              {monthGroup.classes.map((item) => {
                const availableSlots = item.maxCapacity - item.currentEnrollment;
                const isFull = availableSlots <= 0;
                const isLowAvailability = availableSlots > 0 && availableSlots < 3;

                return (
                  <div
                    key={item.class.id}
                    className="rounded-lg shadow-md border border-gray-200 dark:border-gray-600 relative"
                    style={{ padding: '15px', backgroundColor: cardBg }}
                  >
                    <h4 className={`font-medium ${getTextColorClass(cardBg)}`}>
                      {item.class.title}
                    </h4>
                    <p className={`text-sm ${getSubTextColorClass(cardBg)}`}>
                      {item.class.day_of_week ? `${item.class.day_of_week}s, ` : ''}{formatDate(item.startDate)} - {formatDate(item.endDate)}{item.startTime ? `, ${formatTime(item.startTime)}` : ''}
                    </p>
                    {isFull ? (
                      <a
                        href={`/waitlist/${item.class.id}`}
                        className={`inline-block px-4 py-1 rounded-full text-sm font-semibold mt-2 hover:opacity-80 transition-opacity ${getTextColorClass(content?.priceButtonColor || '#e5e7eb')}`}
                        style={{ backgroundColor: content?.priceButtonColor || '#e5e7eb' }}
                      >
                        Join Waitlist
                      </a>
                    ) : item.class.price && (
                      <a
                        href={`/portal/${userId}/enroll/${item.class.id}`}
                        className={`inline-block px-4 py-1 rounded-full text-sm font-semibold mt-2 hover:opacity-80 transition-opacity ${getTextColorClass(content?.priceButtonColor || '#e5e7eb')}`}
                        style={{ backgroundColor: content?.priceButtonColor || '#e5e7eb' }}
                      >
                        ${parseFloat(item.class.price).toFixed(2)}
                      </a>
                    )}
                    {item.maxCapacity > 0 && (
                      <span className={`absolute bottom-2 right-2 text-xs ${isFull ? 'text-red-500 font-medium' : isLowAvailability ? 'text-pink-500 font-medium' : getSubTextColorClass(cardBg)}`}>
                        {isFull ? 'Class Full' : `${availableSlots} spots left`}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default ClassScheduleBlockView;
